import { cur } from '@/lib/currency';
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Clock, DollarSign, Banknote, CreditCard, Building2, 
  PlayCircle, StopCircle, AlertTriangle, CheckCircle2, Loader2, Wallet, MinusCircle
} from 'lucide-react';
import api from '@/lib/api';
import DenominationCounter from '@/components/pos/DenominationCounter';
import { ShiftNonCashActuals } from '@/components/pos/ShiftNonCashActuals';
import { ShiftCashHandover } from '@/components/pos/ShiftCashHandover';

const VARIANCE_TOLERANCE = 5; // SR — variance ≤ this needs no reason

export default function CashierShiftModal({ open, onClose, onShiftChange }) {
  const [currentShift, setCurrentShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [openingCash, setOpeningCash] = useState('');
  const [closingCash, setClosingCash] = useState('');
  const [notes, setNotes] = useState('');
  // iter841 — denomination breakdown + variance acknowledgement.
  const [denoms, setDenoms] = useState({});
  const [useDenoms, setUseDenoms] = useState(false);
  const [varianceAck, setVarianceAck] = useState(false);
  const [varianceReason, setVarianceReason] = useState('');
  const [actuals, setActuals] = useState({});
  const [cashLeft, setCashLeft] = useState('');
  const [proposed, setProposed] = useState(null);
  const [openDiffReason, setOpenDiffReason] = useState('');
  const [mv, setMv] = useState({ type: 'out', amount: '', reason: '' });
  const deviceId = (() => { try { return localStorage.getItem('smarto_device_id') || ''; } catch { return ''; } })();

  useEffect(() => {
    if (open) {
      fetchCurrentShift();
    }
  }, [open]);

  const fetchCurrentShift = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('cashier_token');
      const headers = { Authorization: `Bearer ${token}` };
      const { data } = await api.get('/cashier/shift/current', { headers });
      setCurrentShift(data);
      if (data) {
        setClosingCash(data.expected_cash?.toString() || '');
      } else {
        const userData = JSON.parse(localStorage.getItem('cashier_user') || '{}');
        const { data: po } = await api.get('/cashier/shift/proposed-opening', { headers, params: { branch_id: userData.branch_id, device_id: deviceId || undefined } });
        setProposed(po || null);
        if (po) setOpeningCash(String(po.cash_left_for_next));
      }
    } catch (err) {
      console.error('Failed to fetch shift:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartShift = async () => {
    if (!openingCash || parseFloat(openingCash) < 0) {
      toast.error('Please enter a valid opening cash amount');
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('cashier_token');
      const userData = JSON.parse(localStorage.getItem('cashier_user') || '{}');
      const headers = { Authorization: `Bearer ${token}` };
      
      const { data } = await api.post('/cashier/shift/start', {
        branch_id: userData.branch_id || 'default',
        opening_cash: parseFloat(openingCash),
        notes,
        device_id: deviceId || undefined,
        carried_from_shift_id: proposed?.id,
        opening_difference_reason: openDiffReason.trim() || undefined,
      }, { headers });
      
      setCurrentShift(data);
      toast.success('Shift started successfully!');
      onShiftChange?.(data);
      setOpeningCash('');
      setNotes('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start shift');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEndShift = async () => {
    if (!closingCash || parseFloat(closingCash) < 0) {
      toast.error('Please enter a valid closing cash amount');
      return;
    }
    // iter841 — Variance > tolerance requires an explicit
    // acknowledgement + reason so cashiers can't blindly end shift
    // with a shortage/overage.
    const diff = parseFloat(closingCash) - (currentShift?.expected_cash || 0);
    const outOfTolerance = Math.abs(diff) > VARIANCE_TOLERANCE;
    if (outOfTolerance) {
      if (!varianceAck) {
        toast.error(`Variance of ${cur()} ${diff.toFixed(2)} needs acknowledgement`);
        return;
      }
      if (!varianceReason.trim()) {
        toast.error('Please enter a reason for the variance');
        return;
      }
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('cashier_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const { data } = await api.post('/cashier/shift/end', {
        closing_cash: parseFloat(closingCash),
        cash_left_for_next: cashLeft === '' ? undefined : parseFloat(cashLeft),
        device_id: deviceId || undefined,
        notes,
        denominations: useDenoms ? denoms : {},
        variance_ack: outOfTolerance ? true : false,
        variance_ack_reason: outOfTolerance ? varianceReason.trim() : '',
        actual_payments: Object.fromEntries(Object.entries(actuals).filter(([, v]) => v !== '' && !Number.isNaN(parseFloat(v))).map(([k, v]) => [k, parseFloat(v)])),
      }, { headers });
      
      const dispDiff = data?.cash_difference ?? diff;
      toast.success(
        Math.abs(dispDiff) < 0.5
          ? 'Shift closed — drawer balances perfectly'
          : `Shift closed with ${cur()} ${dispDiff.toFixed(2)} ${dispDiff > 0 ? 'overage' : 'shortage'}`
      );
      onShiftChange?.(null);
      setCurrentShift(null);
      setClosingCash('');
      setNotes('');
      setDenoms({});
      setUseDenoms(false);
      setVarianceAck(false);
      setVarianceReason('');
      setActuals({});
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to end shift');
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (val) => `${cur()} ${(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatTime = (isoStr) => {
    if (!isoStr) return '-';
    return new Date(isoStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          {/* iter427 — a11y title for screen readers */}
          <DialogHeader className="sr-only">
            <DialogTitle>Loading shift</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-12">
            <Loader2 size={32} className="animate-spin text-orange-500" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="shift-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-outfit">
            <Clock size={24} className="text-orange-500" />
            {currentShift ? 'Current Shift' : 'Start New Shift'}
          </DialogTitle>
        </DialogHeader>

        {currentShift ? (
          <div className="space-y-4">
            {/* Active Shift Info */}
            <div className="bg-success/10 border border-success/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <Badge className="bg-success">Active Shift</Badge>
                <span className="text-sm text-success">Started: {formatTime(currentShift.started_at)}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Opening Cash</p>
                  <p className="font-bold text-lg">{formatCurrency(currentShift.opening_cash)}</p>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Total Sales</p>
                  <p className="font-bold text-lg text-success">{formatCurrency(currentShift.total_sales)}</p>
                </div>
              </div>
            </div>

            <ShiftNonCashActuals breakdown={currentShift.payment_breakdown || {}} actuals={actuals} onChange={setActuals} fmt={formatCurrency} />

            {/* Expected Cash */}
            <div className="bg-warning/10 border border-warning/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={18} className="text-warning" />
                <span className="font-medium text-warning">Expected Cash in Drawer</span>
              </div>
              <p className="text-2xl font-bold text-warning" data-testid="expected-cash">{formatCurrency(currentShift.expected_cash)}</p>
              {/* iter841 — Show the calculation transparently: Opening +
                  Cash sales − Cash paid-out. Cashier can see WHY the
                  expected number moved (a bill paid from the drawer,
                  etc). */}
              <div className="mt-2 space-y-0.5 text-[11px] text-warning/90">
                <div className="flex items-center gap-1">
                  <Wallet size={11} /> Opening: {formatCurrency(currentShift.opening_cash)}
                </div>
                <div className="flex items-center gap-1">
                  <Banknote size={11} /> + Cash sales: {formatCurrency(currentShift.payment_breakdown?.cash)}
                </div>
                {(currentShift.cash_paid_out || 0) > 0 && (
                  <div className="flex items-center gap-1" data-testid="cash-paid-out">
                    <MinusCircle size={11} /> − Cash paid out ({currentShift.cash_paid_out_count || 0} expense{(currentShift.cash_paid_out_count || 0) === 1 ? '' : 's'}): {formatCurrency(currentShift.cash_paid_out)}
                  </div>
                )}
                {(currentShift.cash_refunds || 0) > 0 && <div data-testid="shift-cash-refunds">(incl. − cash refunds {formatCurrency(currentShift.cash_refunds)})</div>}
                {(currentShift.cash_in || 0) > 0 && <div data-testid="shift-cash-in">+ Cash added: {formatCurrency(currentShift.cash_in)}</div>}
                {(currentShift.cash_out || 0) > 0 && <div data-testid="shift-cash-out">− Cash removed: {formatCurrency(currentShift.cash_out)}</div>}
                {(currentShift.cash_handed_over || 0) > 0 && <div data-testid="shift-cash-handed-over">− Cash handed over (held for branch): {formatCurrency(currentShift.cash_handed_over)}</div>}
                <div className="pt-1 border-t mt-1" data-testid="shift-noncash">
                  Card {formatCurrency(currentShift.payment_breakdown?.card)} · Bank {formatCurrency(currentShift.payment_breakdown?.bank)} · Online {formatCurrency(currentShift.payment_breakdown?.online)} · Credit {formatCurrency(currentShift.payment_breakdown?.credit)} (not in drawer)
                </div>
              </div>
              <div className="flex gap-1 mt-2" data-testid="cash-movement-form">
                <select className="border rounded px-1 text-xs" value={mv.type} onChange={(e) => setMv({ ...mv, type: e.target.value })} data-testid="cash-movement-type">
                  <option value="out">Cash removed</option><option value="in">Cash added</option>
                </select>
                <Input className="h-7 w-20 text-xs" type="number" placeholder="Amount" value={mv.amount} onChange={(e) => setMv({ ...mv, amount: e.target.value })} data-testid="cash-movement-amount" />
                <Input className="h-7 text-xs" placeholder="Reason (to safe, float — not expenses)" value={mv.reason} onChange={(e) => setMv({ ...mv, reason: e.target.value })} data-testid="cash-movement-reason" />
                <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="cash-movement-save" onClick={async () => {
                  try {
                    await api.post('/cashier/shift/cash-movement', { ...mv, amount: parseFloat(mv.amount) }, { headers: { Authorization: `Bearer ${localStorage.getItem('cashier_token')}` } });
                    setMv({ type: 'out', amount: '', reason: '' }); fetchCurrentShift();
                  } catch (e) { toast.error(e?.response?.data?.detail || 'Not saved'); }
                }}>Save</Button>
              </div>
              <ShiftCashHandover shift={currentShift} onDone={fetchCurrentShift} />
            </div>

            {/* End Shift Form */}
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between">
                  <Label>Closing Cash Count *</Label>
                  {/* iter841 — Toggle denomination-by-denomination
                      counting mode for cashiers who prefer a checklist
                      instead of typing a single total. */}
                  <button
                    type="button"
                    onClick={() => setUseDenoms((v) => !v)}
                    className="text-[11px] text-orange-600 hover:text-orange-800 font-medium"
                    data-testid="toggle-denoms"
                  >
                    {useDenoms ? 'Use single total ↑' : 'Count by note ↓'}
                  </button>
                </div>
                {useDenoms ? (
                  <div className="mt-1">
                    <DenominationCounter
                      values={denoms}
                      currencySymbol={cur()}
                      onChange={(next, total) => {
                        setDenoms(next);
                        setClosingCash(String(total.toFixed(2)));
                      }}
                    />
                  </div>
                ) : (
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{cur()}</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={closingCash}
                      onChange={(e) => setClosingCash(e.target.value)}
                      className="pl-12 h-12 text-lg font-bold"
                      placeholder="0.00"
                      data-testid="closing-cash-input"
                    />
                  </div>
                )}
              </div>
              
              {closingCash !== '' && !Number.isNaN(parseFloat(closingCash)) && (() => {
                const diff = Math.round((parseFloat(closingCash) - (currentShift.expected_cash || 0)) * 100) / 100;
                return (
                  <div className="grid grid-cols-3 gap-2 text-center" data-testid="shift-cash-comparison">
                    <div className="rounded-lg border p-2">
                      <div className="text-[11px] text-muted-foreground">Expected Cash</div>
                      <div className="font-bold" data-testid="shift-expected-cash">{formatCurrency(currentShift.expected_cash)}</div>
                    </div>
                    <div className="rounded-lg border p-2">
                      <div className="text-[11px] text-muted-foreground">Actual Cash</div>
                      <div className="font-bold" data-testid="shift-actual-cash">{formatCurrency(parseFloat(closingCash))}</div>
                    </div>
                    <div className={`rounded-lg border p-2 ${diff < 0 ? 'bg-error/10 border-error/30' : diff > 0 ? 'bg-success/10 border-success/30' : ''}`}>
                      <div className="text-[11px] text-muted-foreground">{diff < 0 ? 'Shortage' : diff > 0 ? 'Excess' : 'Balanced'}</div>
                      <div className="font-bold" data-testid="shift-cash-difference">{formatCurrency(Math.abs(diff))}</div>
                    </div>
                  </div>
                );
              })()}
              {closingCash && parseFloat(closingCash) !== currentShift.expected_cash && (
                <div className={`p-3 rounded-lg ${parseFloat(closingCash) > currentShift.expected_cash ? 'bg-success/10 border-success/30' : 'bg-error/10 border-error/30'} border`} data-testid="variance-card">
                  <div className="flex items-center gap-2">
                    {parseFloat(closingCash) > currentShift.expected_cash ? (
                      <CheckCircle2 size={18} className="text-success" />
                    ) : (
                      <AlertTriangle size={18} className="text-error" />
                    )}
                    <span className="font-medium">
                      Difference: {formatCurrency(parseFloat(closingCash) - currentShift.expected_cash)}
                    </span>
                  </div>
                  <p className="text-xs mt-1 text-muted-foreground">
                    {parseFloat(closingCash) > currentShift.expected_cash ? 'Overage detected' : 'Shortage detected'}
                  </p>
                  {/* iter841 — When variance is above tolerance, require
                      an explicit ack + written reason before the shift
                      can be closed. Keeps managers informed and cashiers
                      accountable. */}
                  {Math.abs(parseFloat(closingCash) - currentShift.expected_cash) > VARIANCE_TOLERANCE && (
                    <div className="mt-3 pt-3 border-t border-current/20 space-y-2">
                      <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <Checkbox checked={varianceAck} onCheckedChange={setVarianceAck} data-testid="variance-ack-checkbox" />
                        <span className="font-medium">I confirm the cash count and accept this variance</span>
                      </label>
                      <Input
                        value={varianceReason}
                        onChange={(e) => setVarianceReason(e.target.value)}
                        placeholder="Reason (e.g. till spillage, missed receipt) *"
                        className="h-9 text-sm"
                        data-testid="variance-reason-input"
                      />
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label>Notes (optional)</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any notes about this shift..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Cash left in drawer for the next cashier (optional)</Label>
                <Input type="number" value={cashLeft} onChange={(e) => setCashLeft(e.target.value)} placeholder="e.g. 200"
                  className="mt-1" data-testid="cash-left-for-next-input" />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button 
                onClick={handleEndShift}
                className="bg-error hover:bg-error"
                disabled={actionLoading}
                data-testid="end-shift-btn"
              >
                {actionLoading ? (
                  <Loader2 size={16} className="animate-spin mr-2" />
                ) : (
                  <StopCircle size={16} className="mr-2" />
                )}
                End Shift
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Start Shift Form */}
            <p className="text-muted-foreground">Count the cash in your drawer to start your shift.</p>
            
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
              <Label className="text-orange-800">Opening Cash Count *</Label>
              <div className="relative mt-2">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">SR</span>
                <Input
                  type="number"
                  step="0.01"
                  value={openingCash}
                  onChange={(e) => setOpeningCash(e.target.value)}
                  className="pl-12 h-14 text-xl font-bold"
                  placeholder="0.00"
                  autoFocus
                  data-testid="opening-cash-input"
                />
              </div>
            </div>
            {proposed && (
              <div className="rounded-lg border border-info/30 bg-info/5 p-2 text-xs space-y-1" data-testid="proposed-opening">
                <div>{proposed.cashier_name || 'Previous cashier'} left <b>{formatCurrency(proposed.cash_left_for_next)}</b> in this drawer. Count it and confirm.</div>
                {openingCash !== '' && Math.abs(parseFloat(openingCash) - proposed.cash_left_for_next) >= 0.01 && (
                  <Input value={openDiffReason} onChange={(e) => setOpenDiffReason(e.target.value)} placeholder="Reason for the difference (required)"
                    className="h-7 text-xs" data-testid="opening-difference-reason" />
                )}
              </div>
            )}

            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any notes for this shift..."
                className="mt-1"
              />
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button 
                onClick={handleStartShift}
                className="bg-success hover:bg-success"
                disabled={actionLoading || !openingCash}
                data-testid="start-shift-btn"
              >
                {actionLoading ? (
                  <Loader2 size={16} className="animate-spin mr-2" />
                ) : (
                  <PlayCircle size={16} className="mr-2" />
                )}
                Start Shift
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
